import React, { useEffect, useRef, useState } from 'react';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { VeralliaPallet } from './VeralliaPallet';
import { VERALLIA_CONVEYORS } from '../../../services/live-interface/verallia-interface-service';

const directionVectors = {
  right: { x: 1, y: 0 },
  down: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
  up: { x: 0, y: -1 }
};

const directionOrder = ['right', 'down', 'left', 'up'];

const directionRotations = {
  right: 0,
  down: 90,
  left: 180,
  up: -90
};

const rotateDirection = (direction, rotation) => {
  const index = directionOrder.indexOf(direction);
  return directionOrder[(index + (rotation === 'right' ? 1 : -1) + directionOrder.length) % directionOrder.length];
};

const movePoint = (point, direction, distance) => ({
  x: point.x + directionVectors[direction].x * distance,
  y: point.y + directionVectors[direction].y * distance
});

const createStraightPart = (point, direction, length, width) => {
  switch (direction) {
    case 'right': return { left: point.x, top: point.y - width / 2, width: length, height: width };
    case 'down': return { left: point.x - width / 2, top: point.y, width, height: length };
    case 'left': return { left: point.x - length, top: point.y - width / 2, width: length, height: width };
    case 'up': return { left: point.x - width / 2, top: point.y - length, width, height: length };
    default: throw new Error(`Unsupported direction: ${direction}`);
  }
};

const getConveyorGeometry = (conveyor) => {
  let direction = conveyor.direction;
  let point = { x: conveyor.start[0], y: conveyor.start[1] + conveyor.width / 2 };
  const renderedParts = [];
  const pathSegments = [];

  conveyor.parts.forEach((part) => {
    if (part.type === 'straight') {
      const end = movePoint(point, direction, part.length);
      renderedParts.push(createStraightPart(point, direction, part.length, conveyor.width));
      pathSegments.push({ start: point, end, direction, length: part.length });
      point = end;
      return;
    }

    const center = movePoint(point, direction, conveyor.width / 2);
    renderedParts.push({ left: center.x - conveyor.width / 2, top: center.y - conveyor.width / 2, width: conveyor.width, height: conveyor.width });
    pathSegments.push({ start: point, end: center, direction, length: conveyor.width / 2 });

    direction = rotateDirection(direction, part.direction);

    const end = movePoint(center, direction, conveyor.width / 2);
    pathSegments.push({ start: center, end, direction, length: conveyor.width / 2 });
    point = end;
  });

  return { renderedParts, pathSegments };
};

const getPalletStyle = (pallet, pathSegments, locations) => {
  const locationIndex = Number(pallet.progress);
  if (!Number.isInteger(locationIndex) || locationIndex < 0 || locationIndex >= locations.length) return null;

  const locationPercent = locations[locationIndex];
  if (locationPercent === -1) return null;

  const totalLength = pathSegments.reduce((total, segment) => total + segment.length, 0);
  const targetDistance = totalLength * Math.min(Math.max(locationPercent, 0), 100) / 100;

  let accumulatedDistance = 0;
  let currentSegment = pathSegments[pathSegments.length - 1];

  for (const segment of pathSegments) {
    if (targetDistance <= accumulatedDistance + segment.length) {
      currentSegment = segment;
      break;
    }

    accumulatedDistance += segment.length;
  }

  const distance = Math.min(Math.max(targetDistance - accumulatedDistance, 0), currentSegment.length);
  const center = movePoint(currentSegment.start, currentSegment.direction, distance);
  const isHorizontal = ['right', 'left'].includes(currentSegment.direction);
  const width = isHorizontal ? pallet.length : pallet.width;
  const height = isHorizontal ? pallet.width : pallet.length;

  return { left: center.x - width / 2, top: center.y - height / 2, width, height };
};

const getLoadingStyle = (conveyor, pathSegments) => {
  if (!conveyor.loading || conveyor.parts.at(-1)?.type !== 'straight') return null;

  const lastSegment = pathSegments[pathSegments.length - 1];
  const loadingLength = lastSegment.length * Math.min(Math.max(conveyor.loading.length, 0), 100) / 100;
  const thickness = 10;
  const isHorizontal = ['right', 'left'].includes(lastSegment.direction);

  if (isHorizontal && !['up', 'down'].includes(conveyor.loading.side)) return null;
  if (!isHorizontal && !['left', 'right'].includes(conveyor.loading.side)) return null;

  if (isHorizontal) {
    const left = lastSegment.direction === 'right' ? lastSegment.end.x - loadingLength : lastSegment.end.x;
    const top = conveyor.loading.side === 'up' ? lastSegment.end.y - conveyor.width / 2 - thickness : lastSegment.end.y + conveyor.width / 2;
    return { left, top, width: loadingLength, height: thickness };
  }

  const left = conveyor.loading.side === 'left' ? lastSegment.end.x - conveyor.width / 2 - thickness : lastSegment.end.x + conveyor.width / 2;
  const top = lastSegment.direction === 'down' ? lastSegment.end.y - loadingLength : lastSegment.end.y;
  return { left, top, width: thickness, height: loadingLength };
};

const getDirectionArrowStyle = (conveyor, pathSegments) => {
  if (conveyor.parts[0]?.type !== 'straight' || pathSegments.length === 0) return null;

  const firstSegment = pathSegments[0];
  const margin = 20;
  const width = 64;
  const height = 36;
  const center = movePoint(firstSegment.start, firstSegment.direction, margin + width / 2);

  return {
    left: center.x - width / 2,
    top: center.y - height / 2,
    width,
    height,
    rotation: directionRotations[firstSegment.direction]
  };
};

const getSceneBounds = (renderedParts, palletStyles, loadingStyle, padding) => {
  const elements = [...renderedParts, ...palletStyles];
  if (loadingStyle !== null) elements.push(loadingStyle);

  const left = Math.min(...elements.map(({ left }) => left));
  const top = Math.min(...elements.map(({ top }) => top));
  const right = Math.max(...elements.map(({ left, width }) => left + width));
  const bottom = Math.max(...elements.map(({ top, height }) => top + height));

  return { left: left - padding, top: top - padding, width: right - left + padding * 2, height: bottom - top + padding * 2 };
};

const useElementSize = () => {
  const elementRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return undefined;

    const updateSize = () => {
      const { width, height } = element.getBoundingClientRect();
      setSize({ width, height });
    };

    const observer = new ResizeObserver(updateSize);
    updateSize();
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return { elementRef, ...size };
};

export const VeralliaConveyor = ({ lineId, pallets = [], activeDefault = null }) => {
  const { elementRef, width: availableWidth, height: availableHeight } = useElementSize();
  const conveyor = VERALLIA_CONVEYORS[lineId];

  if (!conveyor) return null;

  const { renderedParts, pathSegments } = getConveyorGeometry(conveyor);
  const displayedPallets = pallets.map((pallet) => ({ pallet, style: getPalletStyle(pallet, pathSegments, conveyor.locations) })).filter(({ style }) => style !== null);
  const palletStyles = displayedPallets.map(({ style }) => style);
  const loadingStyle = getLoadingStyle(conveyor, pathSegments);
  const directionArrowStyle = getDirectionArrowStyle(conveyor, pathSegments);
  const bounds = getSceneBounds(renderedParts, palletStyles, loadingStyle, 10);
  const scale = availableWidth > 0 && availableHeight > 0 ? Math.min(availableWidth / bounds.width, availableHeight / bounds.height) : 1;
  const overlap = scale > 0 ? 1.5 / scale : 1.5;
  const left = (availableWidth - bounds.width * scale) / 2;
  const top = (availableHeight - bounds.height * scale) / 2;

  return (
    <div ref={elementRef} className="relative h-full w-full !overflow-visible">
      <div className="absolute" style={{ left, top, width: bounds.width, height: bounds.height, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
        {renderedParts.map((part, index) => (
          <div
            key={index}
            className="absolute bg-neutral-600"
            style={{
              left: part.left - bounds.left - overlap / 2,
              top: part.top - bounds.top - overlap / 2,
              width: part.width + overlap,
              height: part.height + overlap
            }}
          />
        ))}

        {directionArrowStyle !== null && (
          <ArrowRightIcon
            className="absolute z-10 text-neutral-300"
            strokeWidth={1}
            style={{
              left: directionArrowStyle.left - bounds.left,
              top: directionArrowStyle.top - bounds.top,
              width: directionArrowStyle.width,
              height: directionArrowStyle.height,
              transform: `rotate(${directionArrowStyle.rotation}deg)`
            }}
          />
        )}

        {loadingStyle !== null && (
          <div
            className="absolute bg-neutral-400"
            style={{
              left: loadingStyle.left - bounds.left,
              top: loadingStyle.top - bounds.top,
              width: loadingStyle.width,
              height: loadingStyle.height
            }}
          />
        )}

        {displayedPallets.map(({ pallet, style }) => (
          <VeralliaPallet key={pallet.id} palletId={pallet.id} defaults={pallet.defaults ?? []} palletStyle={style} bounds={bounds} frontDirection={conveyor.loading?.side} activeDefault={activeDefault} />
        ))}
      </div>

      <style>{`
        @keyframes verallia-pallet-blink {
          0%, 49% { opacity: 1; }
          50%, 100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
};